// src/pages/ProjectManager.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import Loading from "../components/Loading";
import SetoresManager from "../components/SetoresManager";
import WorkspaceManager from "../components/WorkspaceManager"; // 1) Import adicionado
import ProjectForm from "../components/ProjectForm";
import ContainerGrid from "../components/ContainerGrid";
import EntityDetails from "../components/EntityDetails";
import backImg from "../assets/back.png";
import back1Img from "../assets/back1.png";
import back2Img from "../assets/back2.png";
import back3Img from "../assets/back3.png";
import backxImg from "../assets/backx.png";
import "./Containers.css";

const DEFAULT_SECTION_ORDER = ["projetos", "setores", "workspaces"];
const DEFAULT_COLUMN_WIDTHS = { projetos: 33.34, setores: 33.33, workspaces: 33.33 };

export default function ProjectManager({ containerAtual, user, onSidebarUpdate, searchTerm = "" }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [projects, setProjects] = useState([]);
  const [setores, setSetores] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSetor, setSelectedSetor] = useState(null);
  const [setorDetalhado, setSetorDetalhado] = useState(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [workspaceDetalhado, setWorkspaceDetalhado] = useState(null);
  const [workspaceEmEdicao, setWorkspaceEmEdicao] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSetoresModal, setShowSetoresModal] = useState(false);
  const [showWorkspaceModal, setShowWorkspaceModal] = useState(false); // 4) Estado adicionado
  const [workspaceIdEmEdicao, setWorkspaceIdEmEdicao] = useState(null); // 4) Estado adicionado
  const [menuSetorAberto, setMenuSetorAberto] = useState(null);
  const [menuWorkspaceAberto, setMenuWorkspaceAberto] = useState(null);
  const [background, setBackground] = useState("#f1f1f1ff");
  const [showBackgroundMenu, setShowBackgroundMenu] = useState(false);
  const [profile, setProfile] = useState(null);
  const [initialFormData, setInitialFormData] = useState(null);
  const [error, setError] = useState(null);
  const [setorEmEdicao, setSetorEmEdicao] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [gerenteContainerId, setGerenteContainerId] = useState(null);
  const [sectionOrder, setSectionOrder] = useState(DEFAULT_SECTION_ORDER);
  const [layoutModo, setLayoutModo] = useState("linhas");
  const [columnWidths, setColumnWidths] = useState(DEFAULT_COLUMN_WIDTHS);

  const userId = user?.id;

  useEffect(() => {
    setCurrentUserId(userId);
  }, [userId]);

  useEffect(() => {
    const loadProfile = async () => {
      if (!containerAtual) return;
      const { data, error } = await supabase
        .from("profiles")
        .select("nome")
        .eq("id", containerAtual)
        .single();
      if (!error) setProfile(data);
    };
    loadProfile();
  }, [containerAtual]);

  useEffect(() => {
    setSelectedProject(null);
    setSelectedSetor(null);
    setSetorDetalhado(null);
    setSelectedWorkspace(null);
    setWorkspaceDetalhado(null);
  }, [containerAtual]);

  const handleSetBackground = async (value) => {
    setBackground(value);
    setShowBackgroundMenu(false);
    if (containerAtual) {
      const { error } = await supabase
        .from("profiles")
        .update({ background: value })
        .eq("id", containerAtual);
      if (error) setBackground("#f1f1f1ff");
    }
  };

  const fetchProjects = async (userId) => {
    if (!userId) {
      setProjects([]);
      return;
    }

    try {
      const { data: projectsData, error: projectsError } = await supabase
        .from("projects")
        .select(`*, pavimentos(*), eap(*)`)
        .eq("user_id", userId)
        .order("ordem_display", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: false });

      if (projectsError) throw projectsError;

      const enhancedProjects = [];
      for (const proj of projectsData || []) {
        try {
          let photoUrl = proj.photo_url || null;

          if (!photoUrl) {
            const { data: photoData } = await supabase
              .from("projects_photos")
              .select("photo_url")
              .eq("project_id", proj.id)
              .order("created_at", { ascending: false })
              .limit(1)
              .single();
            photoUrl = photoData?.photo_url || null;
          }

          const { data: membersData } = await supabase
            .from("project_members")
            .select("user_id")
            .eq("project_id", proj.id);

          let membrosDetalhados = [];
          if (membersData?.length) {
            const userIds = membersData.map(m => m.user_id);
            const { data: perfis } = await supabase
              .from("profiles")
              .select("id, nickname, avatar_url")
              .in("id", userIds);
            membrosDetalhados = perfis || [];
          }

          const sortedPavimentos = [...(proj.pavimentos || [])].sort(
            (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)
          );
          const sortedEap = [...(proj.eap || [])].sort(
            (a, b) => (a.ordem ?? 0) - (b.ordem ?? 0)
          );

          enhancedProjects.push({
            ...proj,
            pavimentos: sortedPavimentos,
            eap: sortedEap,
            photo_url: photoUrl,
            membros: membrosDetalhados,
          });
        } catch (err) {
          enhancedProjects.push({
            ...proj,
            pavimentos: proj.pavimentos || [],
            eap: proj.eap || [],
            photo_url: proj.photo_url || null,
            membros: [],
          });
        }
      }
      setProjects(enhancedProjects);
    } catch (err) {
      setError("Erro ao carregar projetos");
      setProjects([]);
    }
  };

  const fetchSetores = async (userId) => {
    if (!userId) {
      setSetores([]);
      return;
    }

    try {
      const { data: setoresData } = await supabase
        .from("setores")
        .select("*")
        .eq("user_id", userId)
        .order("ordem_display", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true });

      const setoresComFoto = await Promise.all(
        (setoresData || []).map(async (setor) => {
          const { data: photoData } = await supabase
            .from("setores_photos")
            .select("photo_url")
            .eq("setor_id", setor.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .single();
          return { ...setor, photo_url: photoData?.photo_url || null };
        })
      );
      setSetores(setoresComFoto);
    } catch (err) {
      setError("Erro ao carregar setores");
      setSetores([]);
    }
  };

  const fetchWorkspaces = async (userId) => {
    if (!userId) {
      setWorkspaces([]);
      return;
    }
    try {
      const { data, error } = await supabase
        .from("workspaces")
        .select("*")
        .eq("user_id", userId)
        .order("ordem_display", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true });
      if (error) throw error;
      setWorkspaces(data || []);
    } catch (err) {
      console.error("Erro ao carregar workspaces:", err);
      setWorkspaces([]);
    }
  };

  const loadSetorDetails = async (setor) => {
    setLoading(true);
    try {
      const { data: membersData } = await supabase
        .from("setor_members")
        .select("user_id")
        .eq("setor_id", setor.id);

      let membrosDetalhados = [];
      if (membersData?.length) {
        const userIds = membersData.map(m => m.user_id);
        const { data: perfis } = await supabase
          .from("profiles")
          .select("id, nickname, avatar_url")
          .in("id", userIds);
        membrosDetalhados = perfis || [];
      }
      setSetorDetalhado({ ...setor, membros: membrosDetalhados });
    } catch (err) {
      console.error("Erro ao carregar membros do setor:", err);
      setSetorDetalhado({ ...setor, membros: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!containerAtual) {
      setLoading(false);
      setError("Usuário não identificado");
      return;
    }

    setLoading(true);
    setError(null);

    const loadAll = async () => {
      try {
        const { data: profileData } = await supabase
          .from("profiles")
          .select("background, gerente_container_id, secoes_ordem, layout_modo, colunas_larguras")
          .eq("id", containerAtual)
          .single();

        setBackground(profileData?.background || "#f1f1f1ff");
        setGerenteContainerId(profileData?.gerente_container_id);

        const ordemSalva = Array.isArray(profileData?.secoes_ordem) && profileData.secoes_ordem.length === 3
          ? profileData.secoes_ordem
          : DEFAULT_SECTION_ORDER;
        setSectionOrder(ordemSalva);

        setLayoutModo(profileData?.layout_modo === "colunas" ? "colunas" : "linhas");

        const larguraSalva = profileData?.colunas_larguras;
        setColumnWidths(
          larguraSalva && typeof larguraSalva === "object"
            ? { ...DEFAULT_COLUMN_WIDTHS, ...larguraSalva }
            : DEFAULT_COLUMN_WIDTHS
        );

        await Promise.all([
          fetchProjects(containerAtual),
          fetchSetores(containerAtual),
          fetchWorkspaces(containerAtual),
        ]);
      } catch (err) {
        setError("Falha ao carregar dados do container");
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [containerAtual]);

  const hasContainerEditPermissions = currentUserId && (
    currentUserId === containerAtual ||
    currentUserId === gerenteContainerId
  );

  const canEditEntity = (entity) => {
    if (!currentUserId || !entity) return false;
    if (hasContainerEditPermissions) return true;
    return currentUserId === entity.gerente_caixa_id;
  };

  // ── Ordem das seções (Projetos/Setores/Workspaces) ──
  const handleReorderSections = useCallback(async (novaOrdem) => {
    setSectionOrder(novaOrdem);
    if (!containerAtual) return;
    try {
      await supabase
        .from("profiles")
        .update({ secoes_ordem: novaOrdem })
        .eq("id", containerAtual);
    } catch (err) {
      console.error("Erro ao salvar ordem das seções:", err);
    }
  }, [containerAtual]);

  // ── Layout: linhas x colunas ──
  const handleToggleLayoutModo = useCallback(async () => {
    const novoModo = layoutModo === "colunas" ? "linhas" : "colunas";
    setLayoutModo(novoModo);
    if (!containerAtual) return;
    try {
      await supabase.from("profiles").update({ layout_modo: novoModo }).eq("id", containerAtual);
    } catch (err) {
      console.error("Erro ao salvar modo de layout:", err);
    }
  }, [layoutModo, containerAtual]);

  const handleResizeColumns = useCallback(async (novasLarguras) => {
    setColumnWidths(novasLarguras);
    if (!containerAtual) return;
    try {
      await supabase.from("profiles").update({ colunas_larguras: novasLarguras }).eq("id", containerAtual);
    } catch (err) {
      console.error("Erro ao salvar larguras das colunas:", err);
    }
  }, [containerAtual]);

  // === Projetos ===
  const handleSaveProjectInterno = async (formData) => {
    const {
      name,
      type,
      pavimentos,
      eap,
      photoFile,
      membrosSelecionados,
      engenheiroResponsavel,
      dataInicio,
      dataFinalizacao
    } = formData;

    let currentProjectId = isEditing ? selectedProject?.id : null;
    let projectResult;

    const projectData = {
      name,
      type: type || "vertical",
      engenheiro_id: engenheiroResponsavel?.id || null,
      data_inicio: dataInicio || null,
      data_finalizacao: dataFinalizacao || null
    };

    if (isEditing && selectedProject) {
      const { data, error } = await supabase
        .from("projects")
        .update(projectData)
        .eq("id", selectedProject.id)
        .select()
        .single();
      if (error) throw error;
      projectResult = data;
    } else {
      const { data, error } = await supabase
        .from("projects")
        .insert([{ ...projectData, user_id: containerAtual }])
        .select()
        .single();
      if (error) throw error;
      projectResult = data;
      currentProjectId = data.id;
    }

    if (photoFile) {
      const fileName = `${Date.now()}_${photoFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from("projects_photos")
        .upload(fileName, photoFile, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("projects_photos").getPublicUrl(fileName);
      await supabase.from("projects_photos").insert([
        { project_id: currentProjectId, photo_url: urlData.publicUrl },
      ]);
    }

    await supabase.from("pavimentos").delete().eq("project_id", currentProjectId);
    const pavimentosToInsert = (pavimentos || [])
      .filter(Boolean)
      .map((name, index) => ({ name, project_id: currentProjectId, ordem: index }));
    if (pavimentosToInsert.length > 0) {
      await supabase.from("pavimentos").insert(pavimentosToInsert);
    }

    await supabase.from("eap").delete().eq("project_id", currentProjectId);
    const eapToInsert = (eap || [])
      .filter(Boolean)
      .map((name, index) => ({ name, project_id: currentProjectId, ordem: index }));
    if (eapToInsert.length > 0) {
      await supabase.from("eap").insert(eapToInsert);
    }

    if (membrosSelecionados?.length > 0) {
      if (isEditing) {
        await supabase.from("project_members").delete().eq("project_id", currentProjectId);
      }
      const membrosParaInserir = membrosSelecionados.map(m => ({
        project_id: currentProjectId,
        user_id: m.id,
        added_by: containerAtual
      }));
      const { error: membrosError } = await supabase.from("project_members").insert(membrosParaInserir);
      if (membrosError) throw membrosError;

      const notificacoes = membrosSelecionados.map(m => ({
        user_id: m.id,
        remetente_id: containerAtual,
        mensagem: `${profile?.nome || "Você"} te adicionou ao projeto "${projectResult.name}"`,
        projeto_id: currentProjectId,
        lido: false,
        created_at: new Date().toISOString(),
        tipo: "convite_projeto"
      }));
      await supabase.from("notificacoes").insert(notificacoes);
    }

    await fetchProjects(containerAtual);
  };

  // === Workspaces ===
  const handleSaveWorkspace = async (formData) => {
    const { name, photoFile, engenheiroResponsavel } = formData;

    let currentId = isEditing && workspaceEmEdicao ? workspaceEmEdicao.id : null;

    const workspaceData = {
      name,
      engenheiro_id: engenheiroResponsavel?.id || null,
    };

    let workspaceResult;
    if (isEditing && workspaceEmEdicao) {
      const { data, error } = await supabase
        .from("workspaces")
        .update(workspaceData)
        .eq("id", workspaceEmEdicao.id)
        .select()
        .single();
      if (error) throw error;
      workspaceResult = data;
    } else {
      const { data, error } = await supabase
        .from("workspaces")
        .insert([{ ...workspaceData, user_id: containerAtual }])
        .select()
        .single();
      if (error) throw error;
      workspaceResult = data;
      currentId = data.id;
    }

    if (photoFile) {
      const fileName = `${Date.now()}_${photoFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from("workspaces_photos")
        .upload(fileName, photoFile, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("workspaces_photos").getPublicUrl(fileName);
      await supabase.from("workspaces").update({ photo_url: urlData.publicUrl }).eq("id", currentId);
    }

    setWorkspaceEmEdicao(null);
    await fetchWorkspaces(containerAtual);
  };

  // 2) Nova função handleSaveSetor — inserida logo abaixo de handleSaveWorkspace
  const handleSaveSetor = async (formData) => {
    const { name, photoFile } = formData;

    const { data: setorResult, error } = await supabase
      .from("setores")
      .insert([{ name, user_id: containerAtual }])
      .select()
      .single();
    if (error) throw error;

    if (photoFile) {
      const fileName = `${Date.now()}_${photoFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from("setores_photos")
        .upload(fileName, photoFile, { upsert: true });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("setores_photos").getPublicUrl(fileName);
      await supabase.from("setores_photos").insert([
        { setor_id: setorResult.id, photo_url: urlData.publicUrl },
      ]);
    }

    await fetchSetores(containerAtual);
  };

  // 3) handleSaveProject substituído com o branch "setor"
  const handleSaveProject = async (formData) => {
    if (!formData.name?.trim()) return alert("Digite o nome!");
    setLoading(true);
    try {
      if (formData.tipo === "workspace") {
        await handleSaveWorkspace(formData);
      } else if (formData.tipo === "setor") {
        await handleSaveSetor(formData);
      } else {
        await handleSaveProjectInterno(formData);
      }
      setShowForm(false);
      setIsEditing(false);
      setSelectedProject(null);
      setInitialFormData(null);
    } catch (err) {
      console.error("Erro ao salvar:", err);
      alert("Erro ao salvar.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = useCallback(async (projectId) => {
    if (!window.confirm("Deseja realmente apagar este projeto?")) return;
    setLoading(true);

    try {
      const { data: photoRecords } = await supabase
        .from("projects_photos")
        .select("photo_url")
        .eq("project_id", projectId);
      if (photoRecords?.length) {
        const fileNames = photoRecords.map((p) => p.photo_url.split("/").pop());
        await supabase.storage.from("projects_photos").remove(fileNames);
        await supabase.from("projects_photos").delete().eq("project_id", projectId);
      }

      await supabase.from("pavimentos").delete().eq("project_id", projectId);
      await supabase.from("eap").delete().eq("project_id", projectId);
      await supabase.from("project_members").delete().eq("project_id", projectId);
      await supabase.from("projects").delete().eq("id", projectId);

      setProjects(prev => prev.filter(p => p.id !== projectId));
      if (selectedProject?.id === projectId) setSelectedProject(null);
      await fetchProjects(containerAtual);
    } catch (err) {
      alert("Erro ao excluir projeto.");
    } finally {
      setLoading(false);
    }
  }, [containerAtual, selectedProject]);

  const handleDeleteWorkspace = useCallback(async (workspaceId) => {
    if (!window.confirm("Excluir workspace?")) return;
    setLoading(true);
    try {
      const { data: workspaceRow } = await supabase
        .from("workspaces")
        .select("photo_url")
        .eq("id", workspaceId)
        .single();

      if (workspaceRow?.photo_url) {
        const fileName = workspaceRow.photo_url.split("/").pop();
        await supabase.storage.from("workspaces_photos").remove([fileName]);
      }

      await supabase.from("workspaces").delete().eq("id", workspaceId);
      setWorkspaces(prev => prev.filter(w => w.id !== workspaceId));
      setMenuWorkspaceAberto(null);
      setSelectedWorkspace(null);
      setWorkspaceDetalhado(null);
    } catch (err) {
      alert("Erro ao excluir workspace.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleEditProject = async (project) => {
    const { data: membros } = await supabase
      .from("project_members")
      .select("user_id")
      .eq("project_id", project.id);

    let membrosSelecionados = [];

    if (membros?.length) {
      const userIds = membros.map(m => m.user_id);
      const { data: perfis } = await supabase
        .from("profiles")
        .select("id, nickname, avatar_url")
        .in("id", userIds);
      membrosSelecionados = perfis || [];
    }

    let engenheiroResponsavel = null;
    if (project.engenheiro_id) {
      const { data: engenheiro } = await supabase
        .from("profiles")
        .select("id, nickname, avatar_url")
        .eq("id", project.engenheiro_id)
        .single();
      engenheiroResponsavel = engenheiro;
    }

    const formData = {
      tipo: "projeto",
      name: project.name || "",
      type: project.type || "vertical",
      pavimentos: project.pavimentos?.map((p) => p.name) || [],
      eap: project.eap?.map((e) => e.name) || [],
      photoFile: null,
      photoUrl: project.photo_url || null,
      membrosSelecionados,
      engenheiroResponsavel,
      dataInicio: project.data_inicio || "",
      dataFinalizacao: project.data_finalizacao || "",
    };

    setSelectedProject(project);
    setIsEditing(true);
    setInitialFormData(formData);
    setShowForm(true);
  };

  const handleEditWorkspace = async (workspace) => {
    let engenheiroResponsavel = null;
    if (workspace.engenheiro_id) {
      const { data: engenheiro } = await supabase
        .from("profiles")
        .select("id, nickname, avatar_url")
        .eq("id", workspace.engenheiro_id)
        .single();
      engenheiroResponsavel = engenheiro;
    }

    setWorkspaceEmEdicao(workspace);
    setIsEditing(true);
    setInitialFormData({
      tipo: "workspace",
      name: workspace.name || "",
      engenheiroResponsavel,
      photoUrl: workspace.photo_url || null,
    });
    setShowForm(true);
  };

  const handleOpenSetoresManager = useCallback(() => {
    if (!containerAtual) {
      alert("Usuário não identificado.");
      return;
    }
    setShowSetoresModal(true);
  }, [containerAtual]);

  const handleDeleteSetor = async (setorId) => {
    if (!window.confirm("Excluir setor?")) return;
    setLoading(true);
    try {
      const { data: photoRecords } = await supabase
        .from("setores_photos")
        .select("photo_url")
        .eq("setor_id", setorId);

      if (photoRecords?.length) {
        const fileNames = photoRecords.map((p) => p.photo_url.split("/").pop());
        await supabase.storage.from("setores_photos").remove(fileNames);
        await supabase.from("setores_photos").delete().eq("setor_id", setorId);
      }

      await supabase.from("setor_members").delete().eq("setor_id", setorId);
      await supabase.from("setores").delete().eq("id", setorId);
      setSetores(prev => prev.filter(s => s.id !== setorId));
      setMenuSetorAberto(null);
      setSelectedSetor(null);
      setSetorDetalhado(null);
    } catch (err) {
      alert("Erro ao excluir.");
    } finally {
      setLoading(false);
    }
  };

  const handleReorderProjects = async (reorderedProjects) => {
    try {
      setProjects(reorderedProjects);
      const updates = reorderedProjects.map((proj, index) => ({ id: proj.id, ordem_display: index }));
      for (const update of updates) {
        await supabase.from("projects").update({ ordem_display: update.ordem_display }).eq("id", update.id);
      }
    } catch (err) {
      console.error("Erro ao salvar ordem dos projetos:", err);
    }
  };

  const handleReorderSetores = async (reorderedSetores) => {
    try {
      setSetores(reorderedSetores);
      const updates = reorderedSetores.map((setor, index) => ({ id: setor.id, ordem_display: index }));
      for (const update of updates) {
        await supabase.from("setores").update({ ordem_display: update.ordem_display }).eq("id", update.id);
      }
    } catch (err) {
      console.error("Erro ao salvar ordem dos setores:", err);
    }
  };

  const handleReorderWorkspaces = async (reorderedWorkspaces) => {
    try {
      setWorkspaces(reorderedWorkspaces);
      const updates = reorderedWorkspaces.map((w, index) => ({ id: w.id, ordem_display: index }));
      for (const update of updates) {
        await supabase.from("workspaces").update({ ordem_display: update.ordem_display }).eq("id", update.id);
      }
    } catch (err) {
      console.error("Erro ao salvar ordem dos workspaces:", err);
    }
  };

  const openCardsPage = (proj) => {
    const params = new URLSearchParams({ entityId: proj.id, type: 'project', containerId: containerAtual });
    const urlFinal = `/cards/${encodeURIComponent(proj.name || "Projeto")}?${params.toString()}`;
    const stateData = {
      projectId: proj.id,
      projectName: proj.name,
      projectPhoto: proj.photo_url,
      from: location.pathname,
      containerId: containerAtual,
    };
    navigate(urlFinal, { state: stateData });
  };

  const openSetorCardsPage = (setor) => {
    const params = new URLSearchParams({ entityId: setor.id, type: 'setor', containerId: containerAtual });
    const urlFinal = `/cards/${encodeURIComponent(setor.name || "Setor")}?${params.toString()}`;
    const stateData = {
      setorId: setor.id,
      setorName: setor.name,
      setorPhoto: setor.photo_url,
      entityType: "setor",
      from: location.pathname,
      containerId: containerAtual,
    };
    navigate(urlFinal, { state: stateData });
  };

  const openWorkspaceCardsPage = (workspace) => {
    const params = new URLSearchParams({ entityId: workspace.id, type: 'workspace', containerId: containerAtual });
    const urlFinal = `/cards/${encodeURIComponent(workspace.name || "Workspace")}?${params.toString()}`;
    const stateData = {
      workspaceId: workspace.id,
      workspaceName: workspace.name,
      workspacePhoto: workspace.photo_url,
      entityType: "workspace",
      from: location.pathname,
      containerId: containerAtual,
    };
    navigate(urlFinal, { state: stateData });
  };

  const loadWorkspaceDetails = async (workspace) => {
    // Workspace ainda não tem tabela de membros própria — reaproveita o registro direto.
    // Se EntityDetails.jsx exigir campo "membros", ajustar aqui quando a tabela existir.
    setWorkspaceDetalhado({ ...workspace, membros: [] });
  };

  const onCreateProject = useCallback(() => {
    setIsEditing(false);
    setInitialFormData(null);
    setWorkspaceEmEdicao(null);
    setShowForm(true);
  }, []);

  const onProjectSelect = useCallback((proj) => {
    setSelectedProject(proj);
  }, []);

  useEffect(() => {
    if (onSidebarUpdate) {
      onSidebarUpdate({
        projects,
        setores,
        workspaces,
        selectedProject,
        onCreateProject,
        onProjectSelect,
        onDeleteProject: handleDeleteProject,
        onOpenSetoresManager: handleOpenSetoresManager,
        currentUserId,
        containerOwnerId: containerAtual,
        gerenteContainerId,
        layoutModo,
        onToggleLayoutModo: handleToggleLayoutModo,
        onToggleBackgroundMenu: () => setShowBackgroundMenu((v) => !v),
      });
    }
  }, [
    projects,
    setores,
    workspaces,
    selectedProject,
    onCreateProject,
    onProjectSelect,
    handleDeleteProject,
    handleOpenSetoresManager,
    currentUserId,
    containerAtual,
    gerenteContainerId,
    onSidebarUpdate,
    layoutModo,
    handleToggleLayoutModo,
  ]);

  if (loading) return <Loading />;
  if (error) {
    return (
      <div style={{ padding: "20px", color: "red", textAlign: "center" }}>
        <h3>⚠️ Erro</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <main
      className="containers-main"
      style={{
        background: background.startsWith("#")
          ? background
          : background === backxImg
            ? `url(${background}) center/cover no-repeat`
            : `url(${background}) center/300px auto repeat`,
      }}
    >
      {showBackgroundMenu && (
        <div className="background-menu">
          {[
            { label: "Padrão", value: "#f1f1f1ff" },
            { label: "Preto", value: "#000000" },
            { label: "Branco", value: "#ffffff" },
            { label: "Azul Leve", value: "#e3f2fd" },
            { label: "Verde Leve", value: "#e8f5e9" },
            { label: "Fundo 1", value: backImg },
            { label: "Fundo 2", value: back1Img },
            { label: "Fundo 3", value: back2Img },
            { label: "Fundo 4", value: back3Img },
            { label: "Fundo 5", value: backxImg },
          ].map((opt) => (
            <div key={opt.value} className="background-option" onClick={() => handleSetBackground(opt.value)}>
              <div
                className="background-swatch"
                style={{
                  backgroundColor: opt.value.startsWith("#") ? opt.value : "transparent",
                  backgroundImage: opt.value.startsWith("#") ? "none" : `url(${opt.value})`,
                }}
              />
              {opt.label}
            </div>
          ))}
        </div>
      )}

      {selectedSetor && setorDetalhado ? (
        <EntityDetails
          entityType="setor"
          entity={setorDetalhado}
          onBack={() => setSelectedSetor(null)}
          onEdit={(updated) => {
            setSetorDetalhado((prev) => ({ ...prev, ...updated }));
            setSetores((prev) => prev.map((s) => (s.id === updated.id ? { ...s, ...updated } : s)));
          }}
          canEdit={canEditEntity(setorDetalhado)}
          containerId={containerAtual}
        />
      ) : selectedWorkspace && workspaceDetalhado ? (
        <EntityDetails
          entityType="workspace"
          entity={workspaceDetalhado}
          onBack={() => setSelectedWorkspace(null)}
          onEdit={(updated) => {
            setWorkspaceDetalhado((prev) => ({ ...prev, ...updated }));
            setWorkspaces((prev) => prev.map((w) => (w.id === updated.id ? { ...w, ...updated } : w)));
          }}
          canEdit={canEditEntity(workspaceDetalhado)}
          containerId={containerAtual}
        />
      ) : selectedProject ? (
        <EntityDetails
          entityType="project"
          entity={selectedProject}
          onBack={() => setSelectedProject(null)}
          onEdit={(updated) => {
            setSelectedProject(updated);
            setProjects(prev => prev.map(p => p.id === updated.id ? { ...p, ...updated } : p));
          }}
          canEdit={canEditEntity(selectedProject)}
          containerId={containerAtual}
        >
        </EntityDetails>
      ) : (
        <ContainerGrid
          projects={projects}
          setores={setores}
          workspaces={workspaces}
          searchTerm={searchTerm}
          onProjectClick={openCardsPage}
          onSetorClick={openSetorCardsPage}
          onWorkspaceClick={openWorkspaceCardsPage}
          onSetorAction={(action, setor) => {
            if (action === "verPerfil") {
              setSelectedSetor(setor);
              loadSetorDetails(setor);
            } else if (action === "delete") {
              handleDeleteSetor(setor.id);
            }
          }}
          onWorkspaceAction={(action, workspace) => {
            if (action === "verPerfil") {
              setSelectedWorkspace(workspace);
              loadWorkspaceDetails(workspace);
            } else if (action === "editar") {
              handleEditWorkspace(workspace);
            } else if (action === "delete") {
              handleDeleteWorkspace(workspace.id);
            }
          }}
          menuSetorAberto={menuSetorAberto}
          setMenuSetorAberto={setMenuSetorAberto}
          menuWorkspaceAberto={menuWorkspaceAberto}
          setMenuWorkspaceAberto={setMenuWorkspaceAberto}
          containerId={containerAtual}
          currentUserId={currentUserId}
          onReorderProjects={handleReorderProjects}
          onReorderSetores={handleReorderSetores}
          onReorderWorkspaces={handleReorderWorkspaces}
          sectionOrder={sectionOrder}
          onReorderSections={handleReorderSections}
          layoutModo={layoutModo}
          columnWidths={columnWidths}
          onResizeColumns={handleResizeColumns}
          onViewProjectDetails={(proj) => setSelectedProject(proj)}
        />
      )}

      <ProjectForm
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setIsEditing(false);
          setInitialFormData(null);
          setWorkspaceEmEdicao(null);
        }}
        onSave={handleSaveProject}
        initialData={initialFormData}
        containerAtual={containerAtual}
        profile={profile}
        isEditing={isEditing}
      />

      {showSetoresModal && containerAtual && (
        <SetoresManager
          userId={containerAtual}
          setorId={setorEmEdicao}
          onClose={() => {
            setShowSetoresModal(false);
            setSetorEmEdicao(null);
            fetchSetores(containerAtual);
          }}
        />
      )}

      {/* 4) WorkspaceManager modal adicionado */}
      {showWorkspaceModal && containerAtual && (
        <WorkspaceManager
          userId={containerAtual}
          workspaceId={workspaceIdEmEdicao}
          onClose={() => {
            setShowWorkspaceModal(false);
            setWorkspaceIdEmEdicao(null);
            fetchWorkspaces(containerAtual);
          }}
        />
      )}
    </main>
  );
}